import { useEffect, useRef, useState } from 'react';
import Spacer from '@components/Spacer';
import CollapsibleArea from '@/components/CollapsibleArea';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { IBM_Plex_Mono as ibmPlexMono } from 'next/font/google';
import { FixedSizeList as List } from 'react-window';
import TextInput from '@/components/FormFields/TextInput';
import DatePicker from '@/components/FormFields/DatePicker';
import Button from '@/components/DesignLibrary/Button';
import useNoodleApi from '@/hooks/useNoodleApi';
import { createDataReference, getDataReferenceById } from '@/tsClient/questionnaires';
import { useToast } from '@hooks';
import { TextArea, Select } from '@/components/DesignLibrary/Atoms';
import FieldError from '@/components/FieldError';
import Header from '@/components/DesignLibrary/Header';
import Plus from '@/components/Icons/Plus';
import ProgressIndicator from '@/components/ProgressIndicator';
import { DataReference } from '@/typings/questionnaires-api-models';
import CopyButton from '@/components/CopyButton';

import s from './SetExternalDataForm.module.scss';

const mono = ibmPlexMono({
  subsets: ['latin'],
  weight: '400',
});

type Props = {
  id?: string;
  existingSlug?: string;
  createVersion?: boolean;
  createMode?: boolean;
  dataReferenceVersions: DataReference[] | null;
  onSubmit?: () => void;
  onOpenModal?: () => void;
};

const SetExternalDataForm: React.FC<Props> = ({
  id,
  createMode,
  createVersion,
  dataReferenceVersions,
  existingSlug,
  onSubmit,
  onOpenModal,
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null);

  const JSONComponent = CollapsibleArea;
  const [isTouched, setIsTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentId, setCurrentId] = useState<string | null>(id || null);
  const [date, setDate] = useState<string | null>(null);
  const [slug, setSlug] = useState<string | null>(existingSlug || null);
  const [label, setLabel] = useState<string | null>(null);
  const [version, setVersion] = useState<number | null>(null);
  const [versionLabel, setVersionLabel] = useState<string | null>(null);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [labelError, setLabelError] = useState<string | null>(null);
  const [versionLabelError, setVersionLabelError] = useState<string | null>(null);
  const [jsonLines, setJsonLines] = useState<string[] | null>(null);
  const [newJson, setNewJson] = useState<string | null>(null);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const { getData: createDataReferenceFn } = useNoodleApi(createDataReference, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.INFO, 'External Data Set.'],
  });

  const { data: dataRef, getData: getDataReferenceByIdFn } = useNoodleApi(getDataReferenceById);

  const handleSubmit = async (): Promise<void> => {
    if (slugError || labelError || jsonError || versionLabelError || !slug || !label || !date || !newJson || !versionLabel) {
      if (!newJson) {
        setJsonError('Invalid JSON format');
      }
      setIsTouched(true);
      return;
    }
    setIsSubmitting(true);
    const newDataref = await createDataReferenceFn({
      slug,
    },
    {
      createNewVersion: createVersion,
      data: newJson,
      date,
      label,
      version,
      versionLabel,
    });
    if (newDataref.data) {
      setCurrentId(newDataref.data.id);
    }
    setIsSubmitting(false);
    if (onSubmit) {
      onSubmit();
    }
  };

  const handleJSONChange = (json: string): void => {
    setNewJson(json);

    try {
      JSON.parse(json);
      setJsonError(null);
    } catch {
      setJsonError('Invalid JSON format');
    }
  };

  useEffect(() => {
    if (id) {
      setCurrentId(id);
    }
  }, [id]);

  useEffect(() => {
    if (currentId) {
      getDataReferenceByIdFn({ id: currentId });
    }
  }, [currentId, getDataReferenceByIdFn]);

  useEffect(() => {
    if (dataRef) {
      setSlug(dataRef.slug);
      setDate(dataRef.date);
      setLabel(dataRef.label);
      setVersion(dataRef.version);
      setVersionLabel(dataRef.versionLabel);
      const jsonData = dataRef.data ? JSON.stringify(JSON.parse(dataRef.data as string), null, 2) : '';
      setJsonLines(jsonData.split('\n'));
    }
  }, [dataRef]);

  const Row = ({ index, style }: { index: number; style: React.CSSProperties }): JSX.Element => (
    <div style={style}>
      <SyntaxHighlighter
        language="json"
        customStyle={{
          backgroundColor: 'var(--color-gray-0)',
          fontSize: 14,
          lineHeight: 0,
          margin: 0,
          overflow: 'hidden',
          padding: '8px 16px',
          width: '100%',
        }}
        codeTagProps={{ style: mono.style }}
      >
        {jsonLines ? jsonLines[index] : ''}
      </SyntaxHighlighter>
    </div>
  );

  if (id && !dataRef) {
    return (
      <>
        <Spacer size={64}/>
        <ProgressIndicator isCentered/>
      </>
    );
  }

  return (
    <>
      {dataRef && (
        <Header
          title={dataRef.label}
          actions={[
            {
              icon: Plus,
              label: `Add new version`,
              onClick: () => {
                if (onOpenModal) {
                  onOpenModal();
                }
              },
            },
          ]}
        />
      )}
      <div className={s.setExternalData__wrapper}>
        <div className={s.content}>
          <TextInput
            id={'slug'}
            disabled={Boolean(id) || createVersion || Boolean(existingSlug)}
            value={slug || ''}
            onChange={(newSlug) => {
              setSlug(newSlug);
              setJsonLines(null);
            }}
            label={'Slug'}
            placeholder={`Enter slug`}
            onError={setSlugError}
            isTouched={isTouched}
            required
          />
          {!createMode && dataReferenceVersions && (
            <Select
              compact
              label='Data Set Selection'
              placeholder='Select Data Set'
              options={dataReferenceVersions.map((v) => ({
                key: v.id,
                label: v.versionLabel,
              }))}
              onChange={setCurrentId}
              value={currentId}
            />
          )}
          <TextInput
            id={'label'}
            value={label || ''}
            onChange={setLabel}
            label={'Label'}
            placeholder={`Enter label`}
            onError={setLabelError}
            isTouched={isTouched}
            required
          />
          <TextInput
            id={'versionLabel'}
            value={versionLabel || ''}
            onChange={setVersionLabel}
            label={'Version Label'}
            placeholder={`Enter version label`}
            onError={setVersionLabelError}
            isTouched={isTouched}
            required
          />
          <DatePicker
            value={date}
            onChange={setDate}
            label='Date'
          />
        </div>
        <div className={s.jsonContent} ref={contentRef}>
          {jsonLines
            ? (
              <JSONComponent
                className={s.json}
                title={'Existing JSON'}
              >
                <div style={{ position: 'relative' }}>
                  <CopyButton
                    text={dataRef?.data ? JSON.stringify(JSON.parse(dataRef.data as string), null, 2) : ''}
                    style={{ position: 'absolute', right: 'var(--spacer-sm)', top: 'var(--spacer-sm)' }}>
                    Copy
                  </CopyButton>
                  <List
                    height={200}
                    itemCount={jsonLines.length}
                    itemSize={20}
                    width="100%"
                  >
                    {Row}
                  </List>
                </div>
              </JSONComponent>
            )
            : (
              <>
                <Spacer />
                <span>No existing data</span>
                <Spacer />
              </>
            )}
        </div>
        <div className={s.content}>
          <TextArea
            label=""
            description="JSON Data"
            value={newJson}
            onChange={handleJSONChange}
          />
          {jsonError && <FieldError>{jsonError}</FieldError>}
          <Spacer size={12}/>
          <Button
            onClick={handleSubmit}
            loading={isSubmitting}
            variant='primary'
            fullWidth
            size='md'
          >
            Set External Data JSON
          </Button>
        </div>
      </div>
    </>
  );
};

export default SetExternalDataForm;
